import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";

/* Faithful recreation of the Gonzo Labs / Atlas studio landing page.
   Self-contained dark "blueprint" theme (near-black + yellow), independent of the app theme.
   All CTAs route into the ConstructVision-3D app (login / register). */

const YELLOW = "#FACC15";
const TEAL = "#2DD4BF";
const IMG = "https://app-gonzo.com/screenshots";

const serif = { fontFamily: "'Playfair Display', Georgia, serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };

function MonoLabel({ children, color = "#6B7280", className = "" }) {
  return (
    <span style={{ ...mono, color, letterSpacing: "0.18em" }} className={`text-[11px] font-medium uppercase ${className}`}>
      {children}
    </span>
  );
}

function BlueprintGrid() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <div className="absolute inset-0" style={{
        backgroundImage:
          "linear-gradient(rgba(148,163,184,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.05) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }} />
      {/* geometric blueprint marks */}
      <div className="absolute left-[12%] top-[26%] h-16 w-16 rounded-full border" style={{ borderColor: `${YELLOW}55` }} />
      <div className="absolute right-[16%] top-[16%] h-24 w-24 border" style={{ borderColor: `${TEAL}55` }} />
      <div className="absolute right-[12%] top-[52%] h-0 w-0"
        style={{ borderLeft: "42px solid transparent", borderRight: "42px solid transparent", borderBottom: `72px solid transparent`, borderBottomColor: `${YELLOW}44` }} />
      {[["18%", "66%"], ["22%", "72%"], ["82%", "54%"], ["88%", "54%"], ["17%", "74%"]].map(([l, t], i) => (
        <Plus key={i} className="absolute h-4 w-4" style={{ left: l, top: t, color: "rgba(148,163,184,0.35)" }} />
      ))}
    </div>
  );
}

function Nav({ go }) {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 backdrop-blur-md" style={{ backgroundColor: "rgba(10,10,10,0.8)" }}>
      <nav className="max-w-[1280px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="landing-logo">
          <span className="h-8 w-8 grid place-items-center font-black text-black rounded-[3px]" style={{ backgroundColor: YELLOW, fontFamily: "'Chivo',sans-serif" }}>G</span>
          <span className="leading-none">
            <span className="font-black tracking-tight text-white block" style={{ fontFamily: "'Chivo',sans-serif" }}>GONZO LABS</span>
            <MonoLabel className="!text-[9px] block mt-0.5">// A Studio of Tools</MonoLabel>
          </span>
        </a>
        <div className="hidden md:flex items-center gap-8 text-sm text-gray-300">
          <a href="#features" onClick={scrollTo("features")} className="hover:text-white transition-colors" data-testid="nav-features">Features</a>
          <a href="#apps" onClick={scrollTo("apps")} className="hover:text-white transition-colors" data-testid="nav-apps">Studio Apps</a>
          <a href="#pricing" onClick={scrollTo("pricing")} className="hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => go("/login")} data-testid="nav-signin" className="text-sm text-gray-200 hover:text-white transition-colors px-2 h-10">Sign in</button>
          <button onClick={() => go("/register")} data-testid="nav-tryfree"
            className="h-10 px-4 font-semibold text-black text-sm rounded-[4px] transition-transform active:scale-95"
            style={{ backgroundColor: YELLOW }}>Try free</button>
        </div>
      </nav>
    </header>
  );
}

const STATS = [
  ["01", "Blueprints", "→ 3D in <60s"],
  ["02", "Accuracy", "94% AI"],
  ["03", "Saved", "8 hrs / project"],
  ["04", "Cost", "From $0/mo"],
];

function Hero({ go }) {
  return (
    <section id="top" className="relative">
      <BlueprintGrid />
      <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 pt-20 pb-16">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-8" style={{ backgroundColor: TEAL }} />
          <MonoLabel color={TEAL}>// Studio 01 — Gonzo Labs</MonoLabel>
        </div>
        <h1 style={serif} className="text-white font-medium leading-[0.95] text-5xl sm:text-7xl lg:text-8xl max-w-4xl">
          Software that <span style={{ color: YELLOW }}>builds</span><br />
          before it <span className="italic text-gray-500">ships.</span>
        </h1>
        <p className="mt-8 max-w-xl text-gray-400 text-lg leading-relaxed">
          A studio of tools built for people who make things in the physical world. Every app runs on the same conviction:{" "}
          <span className="text-white font-medium">the software should do the tedious part</span>, so you can do the ambitious part.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <button onClick={() => go("/register")} data-testid="hero-try-free"
            className="h-13 px-6 py-3.5 font-semibold text-black rounded-[4px] flex items-center gap-2 transition-transform active:scale-95"
            style={{ backgroundColor: YELLOW }}>
            Try Atlas free <ArrowUpRight className="h-4 w-4" />
          </button>
          <a href="#features" onClick={(e) => { e.preventDefault(); document.getElementById("features")?.scrollIntoView({ behavior: "smooth" }); }}
            data-testid="hero-tour" className="h-13 px-6 py-3.5 font-semibold text-white border border-white/20 rounded-[4px] hover:bg-white/5 transition-colors">
            Tour the features
          </a>
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {STATS.map(([n, label, val]) => (
            <div key={n} className="border-r border-b border-white/10 p-5" style={{ backgroundColor: "rgba(255,255,255,0.015)" }}>
              <MonoLabel>// {n}</MonoLabel>
              <div className="mt-3"><MonoLabel color="#9CA3AF" className="!tracking-[0.14em]">{label}</MonoLabel></div>
              <div style={serif} className="text-white text-2xl sm:text-3xl mt-2">{val}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FEATURES = [
  { n: "01", cat: "AI Extraction", title: "Drop a plan. Get a building.", body: "GPT-4o Vision + OpenCV Hough tracing pull every wall, door, window, fixture and label off a PDF, photo or whiteboard sketch — in under sixty seconds.", img: `${IMG}/atlas-blueprint.jpg`, tag: "// Atlas · Blueprint Tab" },
  { n: "02", cat: "CAD", title: "SketchUp fluency in the browser.", body: "Wall / rect / circle / door / window / label tools. Undo/redo, Simplify, Straighten, snap-to-grid, dimension chains, exact-typed lengths.", img: `${IMG}/atlas-cad.jpg`, tag: "// Atlas · 2D CAD Editor" },
  { n: "03", cat: "3D Render", title: "Fifteen phases. One click.", body: "Watch the model come together from excavation through finishing. Studio-quality PNG exports and cinematic walkthrough videos.", img: `${IMG}/atlas-3d.jpg`, tag: "// Atlas · 3D Renderer" },
  { n: "04", cat: "On the Map", title: "Real coordinates. Real terrain.", body: "Pick a lot on the map. AI reads soil, slope, vegetation. Place your 3D model on the actual satellite tile with an AI-matched scale.", img: null, tag: "// Place on Map", badge: "37.7749°N · 122.4194°W  ·  SCALE 1:120" },
  { n: "05", cat: "Pay Apps", title: "AIA G702 / G703 generated.", body: "Auto-populate from your last bid. Edit percent-complete inline. Download a branded, landscape PDF that owners actually accept.", img: `${IMG}/atlas-payapps.jpg`, tag: "// Atlas · Pay Apps · AIA G702" },
  { n: "06", cat: "Field", title: "Jobsite intelligence.", body: "Daily logs with NOAA weather. AI-graded jobsite photos flag missing safety gear and estimate percent-complete per phase. LiDAR scans imported straight from an iPhone.", img: `${IMG}/atlas-field.jpg`, tag: "// Atlas · Field · Daily Log" },
  { n: "07", cat: "Collaboration", title: "Teams and clients, together.", body: "Roles for PM / estimator / viewer. White-label /share links let clients browse the plan, model, and bid in read-only. Every action logged in the activity feed.", img: null, tag: "// Activity" },
];

function Features() {
  return (
    <section id="features" className="relative border-t border-white/10">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20">
        <MonoLabel color={YELLOW}>// Flagship · Atlas</MonoLabel>
        <h2 style={serif} className="text-white text-4xl sm:text-5xl mt-4 max-w-3xl">The <span style={{ color: YELLOW }}>Atlas</span> feature set.</h2>
        <p className="text-gray-400 mt-4 max-w-xl text-lg">Construction management + 3D visualization. Everything one operator needs to bid, build, and bill — in one workspace.</p>

        <div className="mt-14 space-y-6">
          {FEATURES.map((f, i) => (
            <div key={f.n} className={`grid lg:grid-cols-2 gap-6 items-center border border-white/10 ${i % 2 ? "lg:[direction:rtl]" : ""}`}
              style={{ backgroundColor: "rgba(255,255,255,0.015)" }}>
              <div className="p-8 lg:p-12 [direction:ltr]">
                <div className="flex items-center gap-3">
                  <MonoLabel color={YELLOW}>{f.n} //</MonoLabel>
                  <MonoLabel color="#9CA3AF">{f.cat}</MonoLabel>
                </div>
                <h3 style={serif} className="text-white text-3xl sm:text-4xl mt-4 leading-tight">{f.title}</h3>
                <p className="text-gray-400 mt-4 leading-relaxed">{f.body}</p>
                {f.badge && (
                  <div className="mt-6 inline-block border border-white/15 px-4 py-2 rounded" style={mono}>
                    <span className="text-xs" style={{ color: TEAL }}>{f.badge}</span>
                  </div>
                )}
                <div className="mt-6"><MonoLabel>{f.tag}</MonoLabel></div>
              </div>
              <div className="p-4 [direction:ltr] min-h-[220px] flex items-center justify-center">
                {f.img ? (
                  <img src={f.img} alt={f.title} loading="lazy"
                    className="w-full h-full object-cover rounded border border-white/10"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <div className="w-full h-full min-h-[200px] rounded border border-white/10 grid place-items-center relative overflow-hidden">
                    <BlueprintGrid />
                    <span style={{ ...serif, color: YELLOW }} className="relative text-2xl">{f.cat}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const APPS = [
  { n: "01", state: "LIVE", name: "Atlas", sub: "Construction OS", body: "Blueprints, 3D models, pay apps, field ops — one workspace.", cta: "Explore Atlas", live: true },
  { n: "02", state: "LIVE", name: "ConstructVision-3D", sub: "Construction Mgmt", body: "Projects, kanban tasks, version-controlled blueprints, KPI dashboards & real-time crew chat — one command center.", cta: "Launch app", live: true },
  { n: "03", state: "SOON", name: "Sketched", sub: "App · 03", body: "An idea worth building. Watch this space.", cta: "Read more" },
  { n: "04", state: "SOON", name: "TBD", sub: "App · 04", body: "The next one lands here.", cta: "Read more" },
];

function Studio({ go }) {
  return (
    <section id="apps" className="relative border-t border-white/10">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20">
        <MonoLabel color={YELLOW}>// The Studio</MonoLabel>
        <h2 style={serif} className="text-white text-4xl sm:text-5xl mt-4">Four apps. <span className="italic text-gray-500">One at a time.</span></h2>
        <p className="text-gray-400 mt-4 max-w-xl text-lg">Every Gonzo Labs app solves one hard problem for one specific operator. No suite tax. No feature bloat. Buy what you need, keep it forever.</p>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {APPS.map((a) => (
            <div key={a.n} className="border-r border-b border-white/10 p-7 flex flex-col" style={{ backgroundColor: "rgba(255,255,255,0.015)" }}>
              <div className="flex items-center gap-2">
                <MonoLabel>// {a.n}</MonoLabel>
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: a.live ? "#22c55e" : "#6B7280" }} />
                <MonoLabel color={a.live ? "#22c55e" : "#6B7280"}>{a.state}</MonoLabel>
              </div>
              <div style={serif} className="text-white text-3xl mt-6">{a.name}</div>
              <MonoLabel color="#9CA3AF" className="mt-1 block">{a.sub}</MonoLabel>
              <p className="text-gray-400 text-sm mt-4 flex-1 leading-relaxed">{a.body}</p>
              <button onClick={() => a.live && go("/register")} disabled={!a.live} data-testid={`app-${a.name.replace(/\s/g, "-")}`}
                className={`mt-6 text-sm font-semibold flex items-center gap-1.5 transition-colors ${a.live ? "text-white hover:text-[#FACC15]" : "text-gray-600 cursor-default"}`}>
                {a.cta} <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TIERS = [
  { name: "Free", price: "$0", per: "forever", cta: "Start free", items: ["1 project", "5 AI uploads / mo", "2D CAD + 3D render"] },
  { name: "Pro", price: "$49", per: "/mo · 7-day trial", cta: "Start Pro trial", featured: true, items: ["Unlimited projects", "100 AI uploads / mo", "Watermark-free exports", "Priority AI"] },
  { name: "Studio", price: "$149", per: "/mo", cta: "Go Studio", items: ["Everything in Pro", "Unlimited AI uploads", "Unlimited Pay Apps", "White-label branding"] },
];

function Pricing({ go }) {
  return (
    <section id="pricing" className="relative border-t border-white/10">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-20">
        <MonoLabel color={YELLOW}>// Pricing</MonoLabel>
        <h2 style={serif} className="text-white text-4xl sm:text-5xl mt-4">No contracts. <span className="italic text-gray-500">No calls.</span></h2>
        <p className="text-gray-400 mt-4 max-w-xl text-lg">Every tier includes the 3D renderer, CAD editor, and blueprint AI. Upgrade for volume + branding + unlocked exports.</p>

        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {TIERS.map((t) => (
            <div key={t.name} className="border p-8 flex flex-col rounded"
              style={{ borderColor: t.featured ? YELLOW : "rgba(255,255,255,0.12)", backgroundColor: t.featured ? "rgba(250,204,21,0.04)" : "rgba(255,255,255,0.015)" }}
              data-testid={`tier-${t.name}`}>
              <MonoLabel color={t.featured ? YELLOW : "#9CA3AF"}>// {t.name}</MonoLabel>
              <div className="mt-4 flex items-baseline gap-2">
                <span style={serif} className="text-white text-5xl">{t.price}</span>
                <span className="text-gray-500 text-sm" style={mono}>{t.per}</span>
              </div>
              <ul className="mt-8 space-y-3 flex-1">
                {t.items.map((it) => (
                  <li key={it} className="text-gray-300 text-sm flex items-center gap-2">
                    <span style={{ color: t.featured ? YELLOW : TEAL }}>·</span> {it}
                  </li>
                ))}
              </ul>
              <button onClick={() => go("/register")} data-testid={`tier-cta-${t.name}`}
                className={`mt-8 h-12 font-semibold rounded-[4px] transition-transform active:scale-95 ${t.featured ? "text-black" : "text-white border border-white/20 hover:bg-white/5"}`}
                style={t.featured ? { backgroundColor: YELLOW } : {}}>
                {t.cta}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="h-7 w-7 grid place-items-center font-black text-black rounded-[3px] text-sm" style={{ backgroundColor: YELLOW, fontFamily: "'Chivo',sans-serif" }}>G</span>
          <MonoLabel color="#9CA3AF">© Gonzo Labs — A Studio of Tools</MonoLabel>
        </div>
        <a href="https://app.emergent.sh/?utm_source=emergent-badge" target="_blank" rel="noreferrer" className="text-gray-500 hover:text-white transition-colors" style={mono}>
          <span className="text-xs">Made with Emergent</span>
        </a>
      </div>
    </footer>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const go = (path) => navigate(path);
  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: "#0A0A0A", fontFamily: "'Manrope',sans-serif" }}>
      <Nav go={go} />
      <Hero go={go} />
      <Features />
      <Studio go={go} />
      <Pricing go={go} />
      <Footer />
    </div>
  );
}
